import { useState } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

export default function useNotification() {
  const { api, AdminId } = useGlobalContext();

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyActionLoadingId, setHistoryActionLoadingId] = useState(null);

  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [sending, setSending] = useState(false);

  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderStatusLoadingId, setOrderStatusLoadingId] = useState(null);

  const [driverProfileLoading, setDriverProfileLoading] = useState(false);

  const shouldShowLocalError = (data) => {
  return (
    data?.responseCode !== "00" &&
    data?.responseCode !== "02" &&
    data?.responseCode !== "03" &&
    data?.responseCode !== "98" &&
    data?.responseCode !== "401"
  );
};

  const sendNotification = async (form, resetForm) => {
    setSending(true);

    try {
      const response = await api.post("/api/User/v1.0/sendNotification", form);

      if (response.data?.responseCode === "00") {
        toast.success("Notification sent successfully!");

        if (typeof resetForm === "function") {
          resetForm();
        }

        return true;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(
          response.data?.responseMessage || "Failed to send notification"
        );
      }

      return false;
    } catch (err) {
      console.error("sendNotification error:", err);
      toast.error("Error sending notification");
      return false;
    } finally {
      setSending(false);
    }
  };

  // ================= BANNER APIs =================

  const fetchBanners = async (setBanners, setLoading) => {
    setLoading(true);

    try {
      const res = await api.post("/api/User/v1.0/getAllBanners", {
        userid: "",
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        setBanners(res.data?.response || []);
        return;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to fetch banners");
      }
    } catch (err) {
      console.error("fetchBanners error:", err);
      toast.error("Error fetching banners");
    } finally {
      setLoading(false);
    }
  };

  const deleteBanner = async (bannerid, onSuccess, setDeletingId) => {
    if (!bannerid) return;

    setDeletingId(bannerid);

    try {
      const res = await api.post("/api/User/v1.0/deleteBanners", {
        bannerid,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success("Banner deleted");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Delete failed");
      }
    } catch (err) {
      console.error("deleteBanner error:", err);
      toast.error("Error deleting banner");
    } finally {
      setDeletingId(null);
    }
  };

  const insertBanner = async (form, onSuccess, setLoading) => {
    setLoading(true);

    try {
      const payload = {
        backgroundColor: form.backgroundColor,
        descriptionColor: form.descriptionColor,
        imageDesctription: form.imageDesctription,
        imageTitle: form.imageTitle,
        imageURL: form.imageURL,
        titleColor: form.titleColor,
      };

      const res = await api.post("/api/User/v1.0/insertBanners", payload);

      if (res.data?.responseCode === "00") {
        toast.success("Banner created successfully!");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Error creating banner!");
      }
    } catch (err) {
      console.error("insertBanner error:", err);
      toast.error("Error creating banner!");
    } finally {
      setLoading(false);
    }
  };

  // ================= NOTIFICATION HISTORY APIs =================

  const getNotifications = async (status = ["all"]) => {
    setHistoryLoading(true);

    try {
      const res = await api.post("/api/Admin/v2.0/getNotifications", {
        adminId: String(AdminId),
        status,
      });

      if (res.data?.responseCode === "00") {
        return res.data?.data || [];
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to fetch notifications"
        );
      }

      return [];
    } catch (err) {
      console.error("getNotifications error:", err);
      toast.error("Error fetching notifications");
      return [];
    } finally {
      setHistoryLoading(false);
    }
  };

  const updateDriverOffDutyRequest = async ({
    status,
    driverId = "",
    notificationId,
    adminRemarks,
    onSuccess,
  }) => {
    setHistoryActionLoadingId(notificationId);

    try {
      const res = await api.post("/api/Admin/v1.0/updateDriverOffDutyRequest", {
        status,
        driverId: driverId || "",
        adminId: String(AdminId),
        notificationId,
        adminRemarks: adminRemarks?.trim() || "",
      });

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || "Request updated successfully"
        );

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to update request");
      }
    } catch (err) {
      console.error("updateDriverOffDutyRequest error:", err);
      toast.error("Error updating request");
    } finally {
      setHistoryActionLoadingId(null);
    }
  };

  // ================= DRIVER APIs =================

  const getDriverProfileDetails = async (driverId) => {
    if (!driverId) return null;

    setDriverProfileLoading(true);

    try {
      const res = await api.post("/api/User/v2.0/driverProfileDetails", {
        driverId,
      });

      if (res.data?.responseCode === "00") {
        return res.data?.response?.[0] || null;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to fetch driver profile"
        );
      }

      return null;
    } catch (err) {
      console.error("getDriverProfileDetails error:", err);
      toast.error("Error fetching driver profile");
      return null;
    } finally {
      setDriverProfileLoading(false);
    }
  };

  const getDriverAllOrders = async ({ driverId, orderId = "" }) => {
    setOrderDetailsLoading(true);

    try {
      const res = await api.post("/api/Store/v1.0/getDriverAllOrders", {
        driverId: driverId || "",
        orderId: orderId || "",
      });

      if (res.data?.responseCode === "00") {
        return res.data?.data || [];
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to fetch order details"
        );
      }

      return [];
    } catch (err) {
      console.error("getDriverAllOrders error:", err);
      toast.error("Error fetching order details");
      return [];
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const updateOrderStatus = async ({ orderId, status, onSuccess }) => {
    if (!orderId) {
      toast.error("Order ID is required");
      return;
    }

    setOrderStatusLoadingId(orderId);

    try {
      const res = await api.post("/api/Store/v1.0/updateOrderStatus", {
        adminId: String(AdminId),
        orderId: String(orderId),
        status,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Order status updated");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to update order status"
        );
      }
    } catch (err) {
      console.error("updateOrderStatus error:", err);
      toast.error("Error updating order status");
    } finally {
      setOrderStatusLoadingId(null);
    }
  };

  return {
    // Send notification
    sendNotification,
    sending,

    // Drivers
    loadingDrivers,
    setLoadingDrivers,

    // History
    historyLoading,
    historyActionLoadingId,
    getNotifications,
    updateDriverOffDutyRequest,

    // Driver profile
    driverProfileLoading,
    getDriverProfileDetails,

    // Driver orders
    orderDetailsLoading,
    getDriverAllOrders,

    // Order status
    orderStatusLoadingId,
    updateOrderStatus,

    // Banner APIs
    fetchBanners,
    deleteBanner,
    insertBanner,
  };
}